<template>
  <router-view/>
</template>

<script>
// 환경 설정의 분 값. 없거나 1 미만·숫자가 아니면 기본값
function idleMinutes(value, fallback) {
  const minutes = parseInt(value, 10);
  return Number.isFinite(minutes) && minutes >= 1 ? minutes : fallback;
}

export default {
  name: 'App',
  data() {
    return {
      inactivityTimer: null,
      // 비활동 자동 로그아웃 시간. 기본 쇼핑몰 10분·관리자 1시간.
      // 개발 PC 에서만 늘리려면 .env.development.local(커밋 안 됨)에 VUE_APP_SHOP_IDLE_MINUTES / VUE_APP_ADMIN_IDLE_MINUTES (분) 지정
      shopInactivityTimeout: idleMinutes(process.env.VUE_APP_SHOP_IDLE_MINUTES, 10) * 60 * 1000,
      adminInactivityTimeout: idleMinutes(process.env.VUE_APP_ADMIN_IDLE_MINUTES, 60) * 60 * 1000,
      activityEvents: ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'],
      lastActivityTime: Date.now(),
      throttleDelay: 1000 // 1초마다만 타이머 리셋 (성능 최적화)
    }
  },
  mounted() {
    this.checkSessionOnStartup();
    this.setupInactivityDetection();
  },
  beforeUnmount() {
    this.removeInactivityDetection();
  },
  methods: {
    // 현재 페이지에 맞는 타임아웃 반환
    getCurrentTimeout() {
      const isAdminPage = window.location.pathname.startsWith('/admin');
      return isAdminPage ? this.adminInactivityTimeout : this.shopInactivityTimeout;
    },

    // 마지막 활동 시간은 관리자·쇼핑몰이 따로 기록한다
    // (같은 브라우저에서 쇼핑몰을 쓴다고 관리자 세션이 연장되지 않도록)
    activityKey() {
      return window.location.pathname.startsWith('/admin') ? 'adminLastActivityTime' : 'shopLastActivityTime';
    },

    // 앱 시작 시 세션 유효성 검사 (컴퓨터 재시작/브라우저 재접속 대응)
    checkSessionOnStartup() {
      localStorage.removeItem('lastActivityTime'); // 예전 공용 키 정리

      const isAdminPage = window.location.pathname.startsWith('/admin');
      const savedTime = localStorage.getItem(this.activityKey());

      if (!savedTime) {
        // 활동 시간 기록이 없으면 현재 시간 저장
        localStorage.setItem(this.activityKey(), Date.now().toString());
        return;
      }

      const elapsed = Date.now() - parseInt(savedTime);
      const timeout = this.getCurrentTimeout();

      if (isAdminPage) {
        const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        if (adminToken && elapsed > timeout) {
          console.log('관리자 세션 만료 (비활성 ' + Math.round(elapsed / 60000) + '분)');
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminLastActivityTime');
          alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
          this.$router.push('/admin/login');
          return;
        }
      } else {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token && elapsed > timeout) {
          console.log('사용자 세션 만료 (비활성 ' + Math.round(elapsed / 60000) + '분)');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user_info');
          localStorage.removeItem('user');
          localStorage.removeItem('shopLastActivityTime');
          alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
          this.$router.push('/login');
          return;
        }
      }

      // 세션 유효하면 활동 시간 갱신
      localStorage.setItem(this.activityKey(), Date.now().toString());
    },

    // 쇼핑몰 회원 로그인 상태 확인 (관리자 로그인 여부와 무관)
    isShopUserLoggedIn() {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      return !!token;
    },

    // 관리자 로그인 상태 확인
    isAdminLoggedIn() {
      const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      return !!adminToken;
    },

    // 비활동 감지 설정
    setupInactivityDetection() {
      // 바인딩된 핸들러 저장 (제거 시 필요)
      this.boundActivityHandler = this.handleUserActivity.bind(this);

      // 활동 이벤트 리스너 추가
      this.activityEvents.forEach(event => {
        document.addEventListener(event, this.boundActivityHandler, { passive: true });
      });

      // 초기 타이머 시작
      this.startInactivityTimer();
    },

    // 비활동 감지 제거
    removeInactivityDetection() {
      // 타이머 클리어
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }

      // 이벤트 리스너 제거
      if (this.boundActivityHandler) {
        this.activityEvents.forEach(event => {
          document.removeEventListener(event, this.boundActivityHandler);
        });
      }
    },

    // 사용자 활동 핸들러 (쓰로틀링 적용)
    handleUserActivity() {
      const now = Date.now();

      // 쓰로틀링: 마지막 활동 후 1초 이내면 무시
      if (now - this.lastActivityTime < this.throttleDelay) {
        return;
      }

      this.lastActivityTime = now;
      localStorage.setItem(this.activityKey(), now.toString());
      this.startInactivityTimer();
    },

    // 비활동 타이머 시작/리셋
    startInactivityTimer() {
      const isAdminPage = window.location.pathname.startsWith('/admin');

      // 로그인 상태가 아니면 무시
      if (isAdminPage ? !this.isAdminLoggedIn() : !this.isShopUserLoggedIn()) {
        return;
      }

      // 기존 타이머 클리어
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }

      // 새 타이머 시작 (사용자: 10분, 관리자: 1시간)
      const timeout = this.getCurrentTimeout();
      this.inactivityTimer = setTimeout(() => {
        this.handleInactivityLogout();
      }, timeout);
    },

    // 비활동으로 인한 자동 로그아웃
    handleInactivityLogout() {
      const isAdminPage = window.location.pathname.startsWith('/admin');

      if (isAdminPage) {
        if (!this.isAdminLoggedIn()) return;

        console.log('1시간 동안 활동이 없어 관리자 자동 로그아웃됩니다.');
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminLastActivityTime');

        alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
        this.$router ? this.$router.push('/admin/login') : (window.location.href = '/admin/login');
      } else {
        if (!this.isShopUserLoggedIn()) return;

        console.log('10분간 활동이 없어 자동 로그아웃됩니다.');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user_info');
        localStorage.removeItem('user');
        localStorage.removeItem('shopLastActivityTime');

        alert('장시간 활동이 없어 자동 로그아웃되었습니다.\n다시 로그인해 주세요.');
        this.$router ? this.$router.push('/login') : (window.location.href = '/login');
      }
    }
  },
  watch: {
    // 라우트 변경 시에도 타이머 리셋 (페이지 이동도 활동으로 간주)
    '$route'() {
      const isAdminPage = window.location.pathname.startsWith('/admin');
      if (isAdminPage ? this.isAdminLoggedIn() : this.isShopUserLoggedIn()) {
        localStorage.setItem(this.activityKey(), Date.now().toString());
        this.startInactivityTimer();
      }
    }
  }
}
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
}
</style>
